import { useState, useEffect, useCallback, useMemo } from "react";
import { 
  Calendar, 
  CalendarDays, 
  Mail, 
  CheckCircle,
  XCircle,
  Clock,
  Trash2,
  Eye,
  Plus,
  Edit,
  Users,
  User,
  LayoutDashboard,
  Upload,
  Image as ImageIcon,
  Images,
  Ticket,
  Building,
  Settings,
  Key,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminEvents({ credentials, userRole }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const canModify = userRole === 'admin';
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    event_date: "",
    start_time: "",
    end_time: "",
    is_public: true,
    image_url: "",
    ticket_price_adult: "",
    ticket_price_child: "",
    adults_only: false,
    is_free_event: false,
    allow_free_booking: false
  });

  const headers = useMemo(() => {
    const auth = btoa(`${credentials.username}:${credentials.password}`);
    return { Authorization: `Basic ${auth}` };
  }, [credentials.username, credentials.password]);

  const fetchEvents = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/events/all`, { headers });
      setEvents(response.data);
    } catch (error) {
      console.error("Error fetching events:", error);
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Only JPEG, PNG, GIF, and WebP are allowed.");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 5MB.");
      return;
    }

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append("file", file);

    try {
      const response = await axios.post(`${API}/upload`, uploadFormData, {
        headers: {
          ...headers,
          "Content-Type": "multipart/form-data"
        }
      });
      setFormData(prev => ({ ...prev, image_url: response.data.url }));
      toast.success("Image uploaded successfully");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Prepare data - convert empty strings to null for price fields
      const submitData = {
        ...formData,
        ticket_price_adult: formData.ticket_price_adult === "" ? null : parseFloat(formData.ticket_price_adult),
        ticket_price_child: formData.ticket_price_child === "" ? null : parseFloat(formData.ticket_price_child)
      };
      
      if (editingEvent) {
        await axios.patch(`${API}/events/${editingEvent.id}`, submitData, { headers });
        toast.success("Event updated");
      } else {
        await axios.post(`${API}/events`, submitData, { headers });
        toast.success("Event created");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchEvents();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to save event";
      toast.error(message);
    }
  };

  const deleteEvent = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/events/${deleteId}`, { headers });
      toast.success("Event deleted");
      setDeleteId(null);
      fetchEvents();
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      event_date: "",
      start_time: "",
      end_time: "",
      is_public: true,
      image_url: "",
      ticket_price_adult: "",
      ticket_price_child: "",
      adults_only: false,
      is_free_event: false,
      allow_free_booking: false
    });
    setEditingEvent(null);
  };

  const openEditDialog = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
      event_date: event.event_date,
      start_time: event.start_time,
      end_time: event.end_time,
      is_public: event.is_public,
      image_url: event.image_url || "",
      ticket_price_adult: event.ticket_price_adult || "",
      ticket_price_child: event.ticket_price_child || "",
      adults_only: event.adults_only || false,
      is_free_event: event.is_free_event || false,
      allow_free_booking: event.allow_free_booking || false
    });
    setIsDialogOpen(true);
  };

  return (
    <div data-testid="admin-events">
      <div className="flex justify-between items-center mb-8">
        <h1 className="heading-lg">Events</h1>
        {canModify && (
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-event-btn">
                <Plus size={18} className="mr-2" /> Add Event
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif">
                {editingEvent ? "Edit Event" : "Add New Event"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="label-heritage">Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="input-heritage"
                  required
                  data-testid="event-title-input"
                />
              </div>
              <div>
                <Label className="label-heritage">Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="input-heritage min-h-[100px]"
                  required
                  data-testid="event-description-input"
                />
              </div>
              <div>
                <Label className="label-heritage">Date</Label>
                <Input
                  type="date"
                  value={formData.event_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, event_date: e.target.value }))}
                  className="input-heritage"
                  required
                  data-testid="event-date-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="label-heritage">Start Time</Label>
                  <Input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                    className="input-heritage"
                    required
                    data-testid="event-start-time-input"
                  />
                </div>
                <div>
                  <Label className="label-heritage">End Time</Label>
                  <Input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                    className="input-heritage"
                    required
                    data-testid="event-end-time-input"
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={formData.is_public}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_public: checked }))}
                  data-testid="event-public-switch"
                />
                <Label className="text-sm">Public Event</Label>
              </div>
              
              {/* Ticket Pricing Section */}
              <div className="border-t border-[#E7E5E4] pt-4 mt-2">
                <Label className="label-heritage mb-3 block">Ticket Pricing (optional)</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-[#78716C]">Adult Price (£)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.ticket_price_adult}
                      onChange={(e) => setFormData(prev => ({ ...prev, ticket_price_adult: e.target.value }))}
                      className="input-heritage"
                      placeholder="0.00"
                      disabled={formData.is_free_event}
                      data-testid="event-adult-price-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-[#78716C]">Child Price (£)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.ticket_price_child}
                      onChange={(e) => setFormData(prev => ({ ...prev, ticket_price_child: e.target.value }))}
                      className="input-heritage"
                      placeholder="0.00"
                      disabled={formData.adults_only || formData.is_free_event}
                      data-testid="event-child-price-input"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-3">
                  <Switch
                    checked={formData.adults_only}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, adults_only: checked, ticket_price_child: checked ? "" : prev.ticket_price_child }))}
                    data-testid="event-adults-only-switch"
                  />
                  <Label className="text-sm">Adults Only Event</Label>
                </div>
                <div className="flex items-center gap-3 mt-3">
                  <Switch
                    checked={formData.is_free_event}
                    onCheckedChange={(checked) => setFormData(prev => ({ 
                      ...prev, 
                      is_free_event: checked,
                      ticket_price_adult: checked ? "" : prev.ticket_price_adult,
                      ticket_price_child: checked ? "" : prev.ticket_price_child,
                      allow_free_booking: checked ? prev.allow_free_booking : false
                    }))}
                    data-testid="event-free-event-switch"
                  />
                  <Label className="text-sm">Free Event</Label>
                </div>
                {formData.is_free_event && (
                  <div className="flex items-center gap-3 mt-3 ml-6 p-3 bg-[#F5F5F0] rounded-lg">
                    <Switch
                      checked={formData.allow_free_booking}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, allow_free_booking: checked }))}
                      data-testid="event-allow-booking-switch"
                    />
                    <div>
                      <Label className="text-sm">Enable Booking/Reservations</Label>
                      <p className="text-xs text-[#78716C]">Allow people to reserve a spot (e.g., table, quiz entry)</p>
                    </div>
                  </div>
                )}
              </div>
              
              <div>
                <Label className="label-heritage flex items-center gap-2">
                  <ImageIcon className="w-4 h-4" />
                  Event Image
                </Label>
                <div className="mt-2">
                  {formData.image_url ? (
                    <div className="relative">
                      <img 
                        src={formData.image_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${formData.image_url}` : formData.image_url} 
                        alt="Event preview" 
                        className="w-full h-40 object-cover rounded-lg border border-[#E7E5E4]"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => setFormData(prev => ({ ...prev, image_url: "" }))}
                      >
                        <X size={14} />
                      </Button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-[#D6D3D1] rounded-lg cursor-pointer hover:border-[#14532D] transition-colors">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        {uploading ? (
                          <div className="spinner" />
                        ) : (
                          <>
                            <Upload className="w-8 h-8 text-[#78716C] mb-2" />
                            <p className="text-sm text-[#78716C]">Click to upload image</p>
                            <p className="text-xs text-[#A8A29E]">JPEG, PNG, GIF, WebP (max 5MB)</p>
                          </>
                        )}
                      </div>
                      <input 
                        type="file" 
                        className="hidden" 
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleImageUpload}
                        disabled={uploading}
                        data-testid="event-image-input"
                      />
                    </label>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" className="btn-primary" data-testid="save-event-btn">
                  {editingEvent ? "Update Event" : "Create Event"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner" />
        </div>
      ) : events.length === 0 ? (
        <Card className="card-heritage">
          <CardContent className="p-12 text-center">
            <CalendarDays className="w-12 h-12 text-[#D6D3D1] mx-auto mb-4" />
            <p className="text-[#78716C]">No events yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {events.map((event) => (
            <Card key={event.id} className="card-heritage" data-testid={`event-${event.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {/* Thumbnail */}
                  <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-[#F5F5F0] border border-[#E7E5E4]">
                    {event.image_url ? (
                      <img 
                        src={event.image_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${event.image_url}` : event.image_url}
                        alt={event.title}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#D6D3D1" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg></div>'; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="w-8 h-8 text-[#D6D3D1]" />
                      </div>
                    )}
                  </div>
                  
                  {/* Event Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="font-semibold text-[#1C1917]">{event.title}</h3>
                      {!event.is_public && (
                        <span className="px-2 py-0.5 bg-[#F5F5F0] text-[#78716C] text-xs rounded">Private</span>
                      )}
                    </div>
                    <p className="text-sm text-[#78716C] mb-2 line-clamp-2">{event.description}</p>
                    <div className="flex flex-wrap gap-3 text-xs text-[#78716C]">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> {event.event_date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {event.start_time} - {event.end_time}
                      </span>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  {canModify && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button variant="outline" size="sm" onClick={() => openEditDialog(event)}>
                        <Edit size={14} className="mr-1" /> Edit
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-[#B91C1C]"
                      onClick={() => setDeleteId(event.id)}
                    >
                      <Trash2 size={14} />
                    </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Event?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the event.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteEvent} className="bg-[#B91C1C] hover:bg-[#991B1B]">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Messages Component
